import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { Message, MessageList } from "@chatscope/chat-ui-kit-react";
import { Box, Button, Grid, IconButton, Stack, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { v4 as uuidv4 } from "uuid";

import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";

import { AmplifySessionManager } from "../../api/AmplifySessionManager";
import { AppContext } from "../../App";
import { CloseIcon, RefreshIcon } from "../../components/Icons/Icons";
import { Thinking } from "../../components/Icons/Thinking";
import { useUser } from "../../components/Menu/hooks/useUser";
import { ChatInputBar } from "../../components/Tilley/ChatInputBar";
import { SettingsContentComponent } from "../../components/Tilley/SettingsContentComponent";
import { MODEL_NAME_BIG_CHAT, MODEL_NAME_SMALL_CHAT } from "../../config/modelConstants";
import { useChatAssistantAttachments } from "../../contexts/useChatAssistantAttachments";
import { getStorageValue, useLocalStorage } from "../../core/useLocalStorageForChat";
import { PROMPT_NAME_DEFAULT } from "./ChatCommandHelper";
import { CustomMessage } from "./CustomMessage";

// const PUBLIC_DATA_PROMPTS = [
//   {
//     main: "What are the coverage options and premiums for [crop] under the [insurance plan] policy?",
//     sub: "Search RMA actuarials",
//   },
//   {
//     main: "When is the sales closing date for [commodity] in [state] for the [year] reinsurance year?",
//     sub: "Search RMA actuarials",
//   },
//   {
//     main: "What are the important deadlines I need to be aware of for [crop] insurance in [state]?",
//     sub: "Search RMA actuarials",
//   },
//   {
//     main: "How is the indemnity calculated for [crop] under the [insurance plan] policy after a natural disaster?",
//     sub: "Research RMA/FSA guidelines",
//   },
// ];

// const MY_DATA_PROMPTS = [
//   {
//     main: "What is my labor budget for Corn?",
//     sub: "Insight into your data",
//   },
//   {
//     main: "Compare my ACTIVE and DRAFT budgets",
//     sub: "Insight into your data",
//   },
//   {
//     main: 'Remember: I turned on irrigation on "Mom\'s Place" June 1',
//     sub: "Record farm events",
//   },
//   {
//     main: "What is expected insurance indemnity for my CORN if yield to count is 120 bu",
//     sub: "Research your farm",
//   },
//   {
//     main: "Compare my FUEL budget to my Schedule F form",
//     sub: "Research your farm",
//   },
// ];

// const getRandomItems = (array, count) => {
//   const shuffled = [...array].sort(() => 0.5 - Math.random());
//   return shuffled.slice(0, count);
// };

// const getRandomPrompts = () => {
//   const publicPrompts = getRandomItems(PUBLIC_DATA_PROMPTS, 2);
//   const myDataPrompts = getRandomItems(MY_DATA_PROMPTS, 3);
//   return [...publicPrompts, ...myDataPrompts];
// };

const MAX_INPUT_LENGTH = 400;

export const ChatPopupHelper = ({
  promptName,
  userMessage,
  onChatCompletion,
  onClose,
  elapsedTime,
  listening,
  onStopRecording,
  onStartRecording,
  showPopup = false,
  isBigChatFlag = false,
  chats: propChats,
  setChats: propSetChats,
  currentChatId: propCurrentChatId,
  setCurrentChatId: propSetCurrentChatId,
  isNewChat = false,
}) => {
  const { globalState, globalDispatch } = useContext(AppContext);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [isWorking, setIsWorking] = useState(false);
  const [chatVoiceModeOn, setChatVoiceModeOn] = useLocalStorage("tilley_chat_voice_mode", false);
  const [isAudioMessageLoading, setIsAudioMessageLoading] = useState(false);
  const [inputMessage, setInputMessage] = useState("");
  const [audioRefs, setAudioRefs] = useState([]);
  const [userName, setUserName] = useState("");
  const [showPrompts, setShowPrompts] = useState(false);
  const { attachments, onChangeRecords } = useChatAssistantAttachments();
  const messageInputRef = useRef(null);
  // Add ref to track the newly created chat ID for audio functions, as state updates might be asynchronous
  const newChatIdRef = useRef(null);

  // Use props when in big chat mode, otherwise use internal state
  const chats = isBigChatFlag ? propChats || [] : null;
  const setChats = isBigChatFlag ? propSetChats : () => {};
  const currentChatId = isBigChatFlag ? propCurrentChatId : null;
  const setCurrentChatId = isBigChatFlag ? propSetCurrentChatId : () => {};

  const MAX_AUDIO_LENGTH = 500;
  const AUDIO_MODE_KEY = "tilley_audio_mode";
  const [systemDefaultPrompt] = []; //useState(() => getRandomPrompts());
  const [audioMode, setAudioMode] = useState(getStorageValue(AUDIO_MODE_KEY, true));
  useEffect(() => {
    setAudioMode(getStorageValue(AUDIO_MODE_KEY, true));
  }, []);
  const handleAudioModeChange = (value) => {
    setAudioMode(value);
  };

  // State for normal chat mode only
  const [messages, setMessages] = useState([]);

  const getActualCurrentChatId = () => {
    if (isBigChatFlag && isNewChat && newChatIdRef.current) {
      return newChatIdRef.current;
    }
    return currentChatId;
  };

  const handleUserError = useCallback(
    (err) => {
      console.warn(err);
      enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
    },
    [enqueueSnackbar]
  );

  useEffect(() => {
    setShowPrompts(false);
    // Reset isWorking when switching chats (including new chat)
    setIsWorking(false);
  }, [currentChatId]);

  const { myUser, activeProducer, activeAgent } = useUser(
    {
      user_role: globalState.user_role,
      producer_token: globalState.producer_token,
      agent_token: globalState.agent_token,
      globalDispatch,
    },
    handleUserError
  );

  const capitalizeFirstLetter = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  useEffect(() => {
    let userName;
    if (activeProducer?.producer_token) {
      userName = `${capitalizeFirstLetter(activeProducer?.first_name)} ${capitalizeFirstLetter(activeProducer?.middle_name)} ${capitalizeFirstLetter(activeProducer?.last_name)}`;
      if (userName.trim() === "") {
        userName = activeProducer?.business_name ?? "";
      }
    }
    if (userName === "" || !userName) {
      if (activeAgent) {
        userName = `${capitalizeFirstLetter(activeAgent?.first_name)} ${capitalizeFirstLetter(activeAgent?.middle_name)} ${capitalizeFirstLetter(activeAgent?.last_name)}`;
        if (userName.trim() === "") {
          userName = activeAgent?.business_name ?? "";
        }
      }
    }
    if (userName === "" || !userName) {
      userName = myUser?.name ?? "";
    }
    setUserName(userName);
  }, [activeProducer, activeAgent, myUser]);

  const registerAudio = (audio) => {
    if (audio && !audioRefs.includes(audio)) {
      setAudioRefs((prev) => [...prev, audio]);
    }
  };

  const validateAndSetInputMessage = (value) => {
    if (value.length > MAX_INPUT_LENGTH) {
      enqueueSnackbar(`You can enter only up to ${MAX_INPUT_LENGTH} characters`, {
        variant: "warning",
        preventDuplicate: true,
      });
      return value.slice(0, MAX_INPUT_LENGTH);
    }
    return value;
  };

  useEffect(() => {
    if (userMessage && userMessage.trim()) {
      setInputMessage(validateAndSetInputMessage(userMessage));
    }
  }, [userMessage]);

  const handleError = (error, enqueueSnackbar, customMessage = null) => {
    console.error(error.consoleMessage || "API Error:", error);
    enqueueSnackbar(customMessage || error.userMessage || "A network or unexpected error occurred.", {
      preventDuplicate: true,
      variant: "error",
    });
  };

  const handleInputChange = useCallback((value) => {
    setInputMessage(validateAndSetInputMessage(value));
  }, []);

  // Unified currentMessages based on mode
  const currentMessages = isBigChatFlag
    ? isNewChat
      ? []
      : (chats.find((chat) => chat.id === currentChatId) || { messages: [] }).messages || []
    : messages;

  // Replaced setCurrentMessages for big chat mode with functional updates directly on setChats
  const setCurrentMessages = isBigChatFlag ? null : setMessages;

  const sendChatCompletion = async (_input) => {
    if (listening) {
      onStopRecording();
    }
    setShowPrompts(false);
    setIsWorking(true);
    setInputMessage("");

    const systemAttachments = [
      {
        name: "System Information",
        content: `Current Date = ${new Date().toLocaleString()}`,
      },
    ];

    const userProfileAttachments = [
      {
        name: "User Profile",
        content: `User Name = ${userName}; User Role = ${globalState?.user_role}`,
      },
    ];

    const newUserMessage = {
      role: "user",
      content: _input,
      sentTime: Date.now(),
      sender: "You",
      direction: "outgoing",
      isLoadingAudio: false,
    };

    if (isBigChatFlag) {
      if (isNewChat) {
        // Create a new chat when user sends first message in new chat mode
        const newChatId = uuidv4();
        const currentTime = Date.now();
        const newChat = {
          id: newChatId,
          title: _input.length > 50 ? `${_input.slice(0, 47)}...` : _input,
          messages: [newUserMessage],
          createdAt: currentTime,
          lastMessageDate: currentTime,
        };
        // Store the new chat ID in ref for audio functions
        newChatIdRef.current = newChatId;
        setChats((prevChats) => [...prevChats, newChat]);
        setCurrentChatId(newChatId);
      } else {
        // Update existing chat
        const currentTime = Date.now();
        setChats((prevChats) =>
          prevChats.map((chat) =>
            chat.id === currentChatId
              ? {
                  ...chat,
                  messages: [...(chat.messages || []), newUserMessage],
                  lastMessageDate: currentTime,
                }
              : chat
          )
        );
      }
    } else {
      setCurrentMessages((prev) => [...prev, newUserMessage]);
    }

    const apiMessages = [
      ...currentMessages.map((msg) => ({ role: msg.role, content: msg.content })),
      { role: "user", content: _input },
    ];

    // Create empty assistant message immediately for streaming
    const assistantMessage = {
      role: "assistant",
      content: "",
      sentTime: Date.now(),
      sender: "Tilley",
      direction: "incoming",
      isLoadingAudio: false,
      reasoning: "",
    };

    // Add empty assistant message to state
    if (isBigChatFlag) {
      const actualCurrentChatId = getActualCurrentChatId();
      const currentTime = Date.now();
      setChats((prevChats) =>
        prevChats.map((chat) =>
          chat.id === actualCurrentChatId
            ? {
                ...chat,
                messages: [...(chat.messages || []), assistantMessage],
                lastMessageDate: currentTime,
              }
            : chat
        )
      );
    } else {
      setCurrentMessages((prev) => [...prev, assistantMessage]);
    }

    try {
      const chatCompletion = await SendChatCompletion_api({
        messages: apiMessages,
        attachments: [...attachments, ...systemAttachments, ...userProfileAttachments],
        onChunk: (content, reasoning) => {
          // Update the assistant message incrementally as chunks arrive
          if (isBigChatFlag) {
            const actualCurrentChatId = getActualCurrentChatId();
            setChats((prevChats) =>
              prevChats.map((chat) => {
                if (chat.id === actualCurrentChatId) {
                  const messages = [...(chat.messages || [])];
                  const lastMessage = messages[messages.length - 1];
                  if (lastMessage && lastMessage.role === "assistant") {
                    lastMessage.content = content;
                    lastMessage.reasoning = reasoning;
                    // Mark reasoning as streaming if there's reasoning content
                    lastMessage.isReasoningStreaming = reasoning && reasoning.trim().length > 0;
                  }
                  return { ...chat, messages };
                }
                return chat;
              })
            );
          } else {
            setCurrentMessages((prev) => {
              const messages = [...prev];
              const lastMessage = messages[messages.length - 1];
              if (lastMessage && lastMessage.role === "assistant") {
                lastMessage.content = content;
                lastMessage.reasoning = reasoning;
                // Mark reasoning as streaming if there's reasoning content
                lastMessage.isReasoningStreaming = reasoning && reasoning.trim().length > 0;
              }
              return [...messages];
            });
          }
        },
      });

      if (!chatCompletion) {
        console.warn("Chat completion returned null.");
        setIsWorking(false);
        return null;
      }

      // Extract content from the response (reasoning already streamed via onChunk)
      const { content: chatContent } = chatCompletion;
      const cleanedString = chatContent?.replace(/```json|```/g, "").trim();
      let parsedData;
      try {
        parsedData = JSON.parse(cleanedString);

        // Check for navigation category in both array and single object cases
        const hasNavigation = (data) => {
          if (Array.isArray(data)) {
            return data.some((item) => item.category === "navigate");
          } else if (typeof data === "object" && data !== null) {
            return data.category === "navigate";
          }
          return false;
        };

        const getNavigationModule = (data) => {
          if (Array.isArray(data)) {
            const navItem = data.find((item) => item.category === "navigate");
            return navItem?.module;
          } else if (typeof data === "object" && data !== null && data.category === "navigate") {
            return data.module;
          }
          return null;
        };

        if (hasNavigation(parsedData)) {
          const module = getNavigationModule(parsedData);
          if (module) {
            // Navigate to /{module}
            navigate(`/${module}`);
            // Add a message to show the navigation is happening
            setMessages((prev) => [
              ...prev,
              {
                role: "assistant",
                content: `Navigating to ${module}...`,
                sentTime: Date.now(),
                sender: "Tilley",
                direction: "incoming",
              },
            ]);
            return;
          }
        }

        if (parsedData.$schema && parsedData.$schema.includes("vega-lite")) {
          // Update the existing assistant message with vega chart
          const vegaContent = `\`\`\`json\n${JSON.stringify(parsedData, null, 2)}\n\`\`\``;
          if (isBigChatFlag) {
            const actualCurrentChatId = getActualCurrentChatId();
            setChats((prevChats) =>
              prevChats.map((chat) => {
                if (chat.id === actualCurrentChatId) {
                  const messages = [...(chat.messages || [])];
                  const lastMessage = messages[messages.length - 1];
                  if (lastMessage && lastMessage.role === "assistant") {
                    lastMessage.content = vegaContent;
                  }
                  return { ...chat, messages };
                }
                return chat;
              })
            );
          } else {
            setCurrentMessages((prev) => {
              const messages = [...prev];
              const lastMessage = messages[messages.length - 1];
              if (lastMessage && lastMessage.role === "assistant") {
                lastMessage.content = vegaContent;
              }
              return [...messages];
            });
          }
          return;
        }
      } catch {
        // Content is already set via streaming, just ensure it's finalized
        // No need to add a new message since we already have one from streaming

        // const isShortText = audioMode === true && cleanedString.length <= MAX_AUDIO_LENGTH;
        // if (isShortText) {
        //   const assistantIndex = currentMessages.length;
        //   await GetSynthesizeSpeech_api(assistantIndex);
        // }
        onChatCompletion?.(cleanedString);
        return;
      }

      if (Array.isArray(parsedData) && parsedData.length > 0) {
        const refreshedRecords = parsedData.map((item) => refreshObjectFromAttachments(item));
        // Use context callback if available, fallback to prop-based callback
        if (onChangeRecords) {
          onChangeRecords(refreshedRecords);
        } else {
          onChatCompletion?.(refreshedRecords);
        }
      } else if (typeof parsedData === "object" && parsedData !== null && Object.keys(parsedData).length > 0) {
        const refreshedRecords = refreshObjectFromAttachments(parsedData);
        // Always pass as array to context callback as requested
        if (onChangeRecords) {
          onChangeRecords([refreshedRecords]);
        } else {
          onChatCompletion?.([refreshedRecords]);
        }
      } else {
        console.warn("Parsed JSON is empty or invalid format. Passing original string.");
        // Content already set via streaming, just call completion callback
        onChatCompletion?.(cleanedString);
      }
    } catch (error) {
      handleError(error, enqueueSnackbar);
    } finally {
      // Clear isReasoningStreaming flag from the last message
      if (isBigChatFlag) {
        const actualCurrentChatId = getActualCurrentChatId();
        setChats((prevChats) =>
          prevChats.map((chat) => {
            if (chat.id === actualCurrentChatId) {
              const messages = [...(chat.messages || [])];
              const lastMessage = messages[messages.length - 1];
              if (lastMessage && lastMessage.role === "assistant") {
                lastMessage.isReasoningStreaming = false;
              }
              return { ...chat, messages };
            }
            return chat;
          })
        );
      } else {
        setCurrentMessages((prev) => {
          const messages = [...prev];
          const lastMessage = messages[messages.length - 1];
          if (lastMessage && lastMessage.role === "assistant") {
            lastMessage.isReasoningStreaming = false;
          }
          return [...messages];
        });
      }

      setIsWorking(false);
      setTimeout(() => {
        if (messageInputRef.current) {
          messageInputRef.current.focus();
        }
      }, 100);
    }
  };

  const refreshObjectFromAttachments = (currentRecord) => {
    Object.keys(currentRecord).forEach((key) => {
      if (currentRecord[key] === "null") {
        currentRecord[key] = null;
      }
    });

    let foundAttachmentItem = null;
    if (attachments) {
      for (const attachment of attachments) {
        const attachmentRecord = attachment?.content?.find((a) => a.id === currentRecord.id);
        if (attachmentRecord) {
          foundAttachmentItem = attachmentRecord;
          break;
        }
      }
    }

    if (foundAttachmentItem) {
      const mergedRecord = { ...foundAttachmentItem };
      Object.keys(currentRecord).forEach((key) => {
        if (key !== "id") {
          mergedRecord[key] = currentRecord[key];
        }
      });
      return mergedRecord;
    } else {
      return currentRecord;
    }
  };

  async function SendChatCompletion_api({ messages, attachments, onChunk }) {
    try {
      // Build request for /v1/responses API (camelCase, OpenAI-compatible)
      const request = {
        model: isBigChatFlag ? null : MODEL_NAME_SMALL_CHAT,
        messages: messages.map((msg) => ({
          role: msg.role,
          content: msg.content,
        })),
        assistant: promptName || PROMPT_NAME_DEFAULT,
        stream: true, // Enable SSE streaming
        temperature: 0.7,
        reasoning: {
          effort: "medium",
        },
      };

      if (attachments && attachments.length > 0) {
        request.attachments = attachments.map((a) => ({
          name: a.name,
          content: typeof a.content === "string" ? a.content : JSON.stringify(a.content),
          doPreload: a.doPreload || false,
        }));
      }

      const sessionManager = new AmplifySessionManager();
      const jwt = await sessionManager.getBearerToken();

      const response = await fetch(import.meta.env.VITE_RGCHAT_API_URL_ROOT + `v1/responses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + jwt,
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw {
          consoleMessage: `API Error: ${response.status} ${response.statusText}`,
          userMessage: `API Error: ${response.status} - ${response.statusText}`,
        };
      }

      // Parse SSE stream with chunk callback
      return await parseSSEStream(response, onChunk);
    } catch (error) {
      console.error("Network or Unexpected Error in SendChatCompletion_api:", error);
      enqueueSnackbar("A network or unexpected error occurred.", { preventDuplicate: true, variant: "error" });
      return null;
    }
  }

  async function parseSSEStream(response, onChunk) {
    const reader = response.body?.getReader();
    if (!reader) throw new Error("No response body");

    const decoder = new TextDecoder();
    let buffer = "";
    let contentResult = "";
    let reasoningResult = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6).trim();

            if (data === "[DONE]") {
              return { content: contentResult, reasoning: reasoningResult };
            }

            try {
              const parsed = JSON.parse(data);
              const delta = parsed.choices?.[0]?.delta;

              if (delta?.content) {
                contentResult += delta.content;
              }

              if (delta?.reasoning) {
                reasoningResult += delta.reasoning;
              }

              // Call onChunk callback with accumulated content for incremental updates
              if (onChunk && (delta?.content || delta?.reasoning)) {
                onChunk(contentResult, reasoningResult);
              }
            } catch (e) {
              console.error("Failed to parse SSE data:", e);
            }
          }
        }
      }

      return { content: contentResult, reasoning: reasoningResult };
    } finally {
      reader.releaseLock();
    }
  }

  async function GetSynthesizeSpeech_api(messageIdx) {
    try {
      const request = {
        tenantId: import.meta.env.VITE_RGCHAT_TENANTID_TILLEY,
        userId: globalState.producer_token,
        sessionId: "00000000-0000-0000-0000-000000000000",
        accessKey: "00000000-0000-0000-0000-000000000000",
      };

      const sessionManager = new AmplifySessionManager();
      const jwt = await sessionManager.getBearerToken();
      setIsAudioMessageLoading(true);
      const response = await fetch(import.meta.env.VITE_RGCHAT_API_URL_ROOT + `Synthesizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "rg-TenantId": import.meta.env.VITE_RGCHAT_TENANTID_TILLEY,
          "rg-UserID": globalState?.producer_token || globalState?.agent_token,
          authorization: "Bearer " + jwt,
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw {
          consoleMessage: `API Error: ${response.status} ${response.statusText}`,
          userMessage: `API Error: ${response.status} - ${response.statusText}`,
        };
      }

      const reader = response.body.getReader();
      const chunks = [];
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        chunks.push(value);
      }

      const audioData = new Uint8Array(chunks.reduce((acc, chunk) => acc.concat(Array.from(chunk)), []));
      const audioBlob = new Blob([audioData], { type: "audio/mpeg" });
      const audio = new Audio(URL.createObjectURL(audioBlob));
      if (isBigChatFlag) {
        setChats((prevChats) => {
          return prevChats.map((chat) => {
            if (chat.id === getActualCurrentChatId()) {
              const updatedMessages = (chat.messages || []).map((msg, idx) => {
                if (idx === messageIdx && msg.direction === "incoming") {
                  return { ...msg, audio };
                }
                return msg;
              });
              return { ...chat, messages: updatedMessages };
            }
            return chat;
          });
        });
      } else {
        setMessages((prev) => {
          const newMessages = [...(prev || [])];
          if (newMessages[messageIdx].direction === "incoming") {
            newMessages[messageIdx].audio = audio;
          }
          return newMessages;
        });
      }
      setIsAudioMessageLoading(false);
      audio.onerror = (error) => {
        throw {
          consoleMessage: `Audio Playback Error: ${error}`,
          userMessage: "Error playing audio.",
        };
      };
    } finally {
      setIsWorking(false);
    }
  }

  const handleGenerateAudio = async (messageIndex) => {
    return;

    // const actualCurrentChatId = getActualCurrentChatId();
    // const currentChat = chats.find((chat) => chat.id === actualCurrentChatId);
    // if (!currentChat) return;
    // const message = currentChat.messages[messageIndex];
    // if (message.direction !== "incoming") {
    //   console.warn("Cannot generate audio for non-incoming message");
    //   return;
    // }
    // setChats((prevChats) => {
    //   const updatedChats = prevChats.map((chat) => {
    //     if (chat.id === actualCurrentChatId) {
    //       const updatedMessages = chat.messages.map((msg, idx) => {
    //         if (idx === messageIndex) {
    //           return { ...msg, isLoadingAudio: true };
    //         }
    //         return msg;
    //       });
    //       return { ...chat, messages: updatedMessages };
    //     }
    //     return chat;
    //   });
    //   return updatedChats;
    // });

    // try {
    //   await GetSynthesizeSpeech_api(messageIndex);
    // } catch (error) {
    //   handleError(error, enqueueSnackbar);
    // } finally {
    //   setChats((prevChats) => {
    //     const updatedChats = prevChats.map((chat) => {
    //       if (chat.id === actualCurrentChatId) {
    //         const updatedMessages = chat.messages.map((msg, idx) => {
    //           if (idx === messageIndex) {
    //             return { ...msg, isLoadingAudio: false };
    //           }
    //           return msg;
    //         });
    //         return { ...chat, messages: updatedMessages };
    //       }
    //       return chat;
    //     });
    //     return updatedChats;
    //   });
    // }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const text = event.clipboardData.getData("text/plain");
    const textarea = event.target;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    const currentText = inputMessage;
    const remainingLength = MAX_INPUT_LENGTH - currentText.length;
    const availableLength = remainingLength + (end - start);

    if (text.length > availableLength) {
      setTimeout(() => {
        enqueueSnackbar(`You can enter only up to ${MAX_INPUT_LENGTH} characters`, {
          variant: "warning",
          preventDuplicate: true,
        });
      }, 0);
    }

    const textToInsert = text.slice(0, availableLength);
    const newText = currentText.substring(0, start) + textToInsert + currentText.substring(end);

    setInputMessage(newText);

    setTimeout(() => {
      textarea.setSelectionRange(start + textToInsert.length, start + textToInsert.length);
    }, 0);
  };

  const handleVoiceButtonClick = () => {
    if (listening) {
      // Check if onStopRecording is a function before calling it
      if (typeof onStopRecording === "function") {
        onStopRecording();
      } else {
        console.warn("onStopRecording is not a function - ensure it is passed as a prop when isBigChatFlag is true");
      }
      setChatVoiceModeOn(false);
    } else {
      // Check if onStartRecording is a function before calling it
      if (typeof onStartRecording === "function") {
        onStartRecording();
      } else {
        console.warn("onStartRecording is not a function - ensure it is passed as a prop when isBigChatFlag is true");
      }
      setChatVoiceModeOn(true);
      pauseAndResetAllAudio(audioRefs);
    }
  };

  useEffect(() => {
    if (showPopup) {
      if (!chatVoiceModeOn && listening) {
        onStopRecording();
      }
    }
  }, [showPopup, chatVoiceModeOn, listening, onStopRecording]);

  const pauseAndResetAllAudio = (audioRefs) => {
    audioRefs.forEach((a) => {
      if (!a.paused) {
        a.pause();
        a.currentTime = 0;
      }
    });
  };

  const refreshMessages = () => {
    pauseAndResetAllAudio(audioRefs);
    onStopRecording();
    setAudioRefs([]);
    if (isBigChatFlag) {
      const actualCurrentChatId = getActualCurrentChatId();
      if (actualCurrentChatId) {
        setChats((prevChats) =>
          prevChats.map((chat) => (chat.id === actualCurrentChatId ? { ...chat, messages: [] } : chat))
        );
      }
    } else {
      setMessages([]);
    }
  };

  // const formatTime = (sec) => {
  //   const s = String(sec).padStart(2, "0");
  //   return `0:${s}`;
  // };
  const setSystemPrompt = (prompt) => {
    if (prompt === "" || typeof prompt !== "string") {
      return "";
    }
    setInputMessage(prompt);
    setShowPrompts(true);
  };

  const inputAreaProps = {
    listening,
    elapsedTime,
    inputMessage,
    handleInputChange,
    messageInputRef,
    handlePaste,
    isWorking,
    onStopRecording,
    setChatVoiceModeOn,
    sendChatCompletion,
    handleVoiceButtonClick,
    audioMode,
  };

  return (
    showPopup && (
      <Box
        boxShadow={2}
        className="tilley-chat-popup"
        sx={{
          width: "100%",
          maxWidth: "50rem",
          margin: "0px auto",
          boxShadow: isBigChatFlag && "none",
          display: currentMessages.length === 0 && isBigChatFlag && "block",
          alignContent: "center",
          ...(showPrompts && {
            animation: "fadeIn 0.3s ease-in-out",
          }),
          "@keyframes fadeIn": {
            from: { opacity: 0, transform: "translateY(10px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
        }}
      >
        <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
          {!isBigChatFlag && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
              borderBottom={1}
              borderColor="divider"
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: "500 !important", fontSize: "1.25rem !important", color: "secondary.dark" }}
              >
                Hey, Tilley Chat
              </Typography>
              <Stack direction="row" spacing={1}>
                <SettingsContentComponent
                  isTilleyChat={true}
                  onAudioModeChange={handleAudioModeChange}
                  audioMode={audioMode}
                />
                <IconButton onClick={refreshMessages}>
                  <RefreshIcon />
                </IconButton>
                <IconButton onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Box>
          )}
          {/* Chat Content */}
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            {currentMessages.length === 0 ? (
              isBigChatFlag ? (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    p: 3,
                  }}
                >
                  <Box sx={{ maxWidth: "55rem", alignSelf: "center" }}>
                    <Typography variant="h5" align="center" sx={{ mb: "1.625rem !important" }}>
                      How can I help you today?
                    </Typography>

                    {!showPrompts && (
                      <Grid
                        container
                        spacing={1}
                        justifyContent="center"
                        sx={{ pb: 4, alignItems: "stretch", gap: { xs: 1, md: 0 } }}
                      >
                        {systemDefaultPrompt?.map((prompt, idx) => (
                          <Grid key={idx} item xs={12} sm={6} md={idx < 2 ? 6 : 4}>
                            <Button
                              fullWidth
                              variant="outlined"
                              onClick={() => setSystemPrompt(prompt.main)}
                              sx={{
                                border: "unset",
                                backgroundColor: "action.hover",
                                borderRadius: "50px",
                                textTransform: "none",
                                display: "flex",
                                flexDirection: "column",
                                height: "100%",
                                padding: "10px 16px",
                                "&:hover": {
                                  backgroundColor: "action.selected",
                                  border: "unset",
                                },
                              }}
                            >
                              <Typography variant="body1" align="center" color="textPrimary" marginBottom={0.5}>
                                {prompt.main}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {prompt.sub}
                              </Typography>
                            </Button>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Box>

                  <ChatInputBar {...inputAreaProps} isBigChatFlag={true} isZeroCurrentMessages={true} />
                </Box>
              ) : (
                <>
                  <Box sx={{ flex: 1, overflowY: "auto" }}>
                    <MessageList>
                      <MessageList.Content
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          height: "100%",
                          textAlign: "center",
                          fontSize: "18px",
                          fontWeight: "600",
                          color: "var(--mui-palette-text-primary)",
                        }}
                      >
                        I can answer questions,
                        <br />
                        fill out forms,
                        <br />
                        and navigate the app.
                        <br />
                        <br />
                        How can I help you?
                      </MessageList.Content>
                    </MessageList>
                  </Box>
                  <ChatInputBar {...inputAreaProps} isBigChatFlag={false} isZeroCurrentMessages={false} />
                </>
              )
            ) : (
              <>
                <Box sx={{ flex: 1, overflowY: "auto" }}>
                  <MessageList
                    className={`message-list ${isBigChatFlag ? "tilley_big_chat" : ""}`}
                    typingIndicator={
                      isWorking && (
                        <Box className="tilley-typing-indicator">
                          <div className="thinking">
                            <Typography variant="body2" color="secondary" className="thinking-text">
                              <Thinking />
                              <span style={{ paddingLeft: "5px" }}>Tilley is thinking...</span>
                            </Typography>
                          </div>
                        </Box>
                      )
                    }
                  >
                    {currentMessages.map((msg, i) => (
                      <CustomMessage
                        as={Message}
                        key={i}
                        message={msg.content}
                        sentTime={msg.sentTime}
                        sender={msg.sender}
                        direction={msg.direction}
                        audio={msg.audio}
                        registerAudio={registerAudio}
                        isAudioMessageLoading={isAudioMessageLoading && i === currentMessages.length - 1}
                        index={i}
                        isBigChatFlag={isBigChatFlag}
                        //onGenerateAudio={() => handleGenerateAudio(i)}
                        reasoning={msg.reasoning}
                        isStreaming={msg.isReasoningStreaming || false}
                      />
                    ))}
                  </MessageList>
                </Box>
                <ChatInputBar {...inputAreaProps} isBigChatFlag={isBigChatFlag} isZeroCurrentMessages={false} />
              </>
            )}
          </Box>
        </Box>
      </Box>
    )
  );
};
